import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Tying place names to county names with geonames.org",
  "description": "",
  "published": "2016-10-04T00:00:00.000Z",
  "redirect_from": ["/blog/place-names-county-names-geonames/swizec/7083"],
  "categories": "Technical",
  "hero": "./img/WMXH9Ti.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Ever had to figure out which county a town falls into? It’s harder than it looks.`}</p>
    <p>{`San Francisco falls in San Francisco County. That’s easy.`}</p>
    <p>{`Los Angeles is in Los Angeles County. Done.`}</p>
    <p>{`New York spans five counties because a borough is the same as a county. And Boston falls in Suffolk County. Menlo Park is in San Mateo and so is Palo Alto. ?`}</p>
    <p>{`Borough, county, parish, census area, municipality and sometimes city (but sometimes not) all mean the same – a county. Oh, and `}<a parentName="p" {...{
        "href": "http://swizec.com/blog/livecoding-24-choropleth-react-js/swizec/7078"
      }}>{`names are not globally unique`}</a>{`, only state-unique. Marvelous.`}</p>
    <p>{`So, how do you tie town-based datasets to county-based datasets? Say you want to compare tech salaries to median household income. I choose this example because that’s what I’m doing. ?`}</p>
    <hr></hr>
    <p>{`The tech salary is a CSV that looks like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`supreme tech solutions llc,software analyst,"75,000","walnut creek, ca",10/25/2012,10/25/2012,certified
supreme tech solutions llc,software analyst,"76,690","brisbane, ca",10/16/2012,10/16/2012,certified
west corporation,software analyst,"85,000","omaha, ne",10/12/2012,10/24/2012,certified
wincere inc,software analyst,"86,133","anaheim, ca",11/01/2012,12/31/2012,certified
`}</code></pre>
    <p>{`Company, job title, base salary, location (town + state), start date, visa succeeded. Supreme Tech Solutions, LLC employed a software analyst for $75,000 in Walnut Creek, CA on October 25th, 2012, and his or her H1B visa went through.`}</p>
    <p>{`The census dataset for median household incomes looks like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`Autauga County,AL,54.366,50.916,57.816
Baldwin County,AL,49.626,46.516,52.736
Barbour County,AL,34.971,32.475,37.467
`}</code></pre>
    <p>{`County, state, median household income, 90% confidence interval lower bound, upper bound. In Autauga County, Alaska, the median household income is $54,366 and 90% of households fall between $50,916 and $57,816.`}</p>
    <p>{`Notice the problem? One has town names, the other has county names. I don’t think census data for specific towns even exists.`}</p>
    <p><a parentName="p" {...{
        "href": "geonames.org"
      }}>{`Geonames.org`}</a>{` to the rescue! They’ve built up a sturdy database of geographical names. Everything from latitude and longitude to interesting meta data like pseudonyms and even landmark names.`}</p>
    <p>{`There’s an API with libraries for many languages, but it doesn’t return county names. I wish it did, because scraping their HTML search results felt dirty. Those do return a county name.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "890px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/8fd1025e5cb527aaf8110926a110066a/c7bb6/WMXH9Ti.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "22.421524663677133%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'90\\'%20viewBox=\\'0%200%20400%2090\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M0%206v5h401V1H0v5m170%207v2l-1%201-2-1c0-2-12-2-13-1h-1l-1-1v3c2%202%202%202%200%202-1%201%200%204%202%204%203%200%205-1%205-2l-2-1h-3l4-1c2-1%203%200%203%201l2%202h16c0-1-3-2-13-2l-4-1h20l2-1c1%200%202%200%201-1l-1-1c0-1-2-2-5-2h-9M15%2017v2h17c16%200%2016%200%2016-2-1-4-22-4-23%200h-1c0-2-2-2-5-3-3%200-4%200-4%203m0%205h2l15%201c25%200%2024%200%2023%201%200%201-1%202-4%201H38l-6%201H15c0%201%2038%202%2043%201%202%200%201-2%200-2l-1-1c0-2-2-3-8-4l-5%201h-1c-3-2-28-1-28%201m78-1l-1%202h30c12%200%2017-1%2012-2H93m86%2018c-1%202-1%202-2%201-1-2-2-2-13-2-12%200-13%200-13%202v2h24l26-1-1-1-1-1c0-2-7-2-9-1h-5l-4-1c-1-1-1%200-2%202M72%2038l-11%201c0-2-4-1-4%201s0%202-1%201c0-3-6-3-6%200l-1%201-1-1c0-2-1-2-4-2a2274%202274%200%2001-19%202h-1c0-2-8-3-9-2v3c-1%201%202%201%2030%201%2030%200%2031%200%2031-2l-1-2-2-1h-1m183%206c-1%201%200%203%204%203l3-1h7l4%201%204-1h1c1%202%2012%201%2012-1%200-1-33-2-35-1M16%2059l-1%203v3h33v-2c0-2-1-2-10-2l-10-1c-1-1-1%200-2%202s-1%202-2%200c-1-1-2-2-3-1l-4-1-1-1m136%201c-3%204%200%205%2011%205%2010%200%2011%200%2010%202%200%202%201%202%204%202l4-2-3-1c-3%201-4%200-1-1%202-1%200-4-4-5h-4c-1%200-2%200-2%202l-1%202-1-2c-1-2-1-2-2-1h-4l-5-1h-2m-1%2016v2h17c17%200%2019%200%2017-3-3-2-16-2-16%200l-1%202-1-2c0-2-12-2-13%200h-1l-1-2-1%203M15%2074v3l-1%202h86l-1-2-1-2H86c-2-1-2%200-3%201%200%202-2%203-2%200l-16-1-15%201-1%202-1-2-3-1H28l-2%201-1%202-1-1-2-2-5-1h-2\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/8fd1025e5cb527aaf8110926a110066a/ca0a1/WMXH9Ti.webp 223w", "/static/8fd1025e5cb527aaf8110926a110066a/75680/WMXH9Ti.webp 445w", "/static/8fd1025e5cb527aaf8110926a110066a/8d1ba/WMXH9Ti.webp 890w", "/static/8fd1025e5cb527aaf8110926a110066a/3838e/WMXH9Ti.webp 1335w", "/static/8fd1025e5cb527aaf8110926a110066a/e11e5/WMXH9Ti.webp 1780w", "/static/8fd1025e5cb527aaf8110926a110066a/ca269/WMXH9Ti.webp 1986w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/8fd1025e5cb527aaf8110926a110066a/e92b6/WMXH9Ti.png 223w", "/static/8fd1025e5cb527aaf8110926a110066a/e66bf/WMXH9Ti.png 445w", "/static/8fd1025e5cb527aaf8110926a110066a/4ef49/WMXH9Ti.png 890w", "/static/8fd1025e5cb527aaf8110926a110066a/4e814/WMXH9Ti.png 1335w", "/static/8fd1025e5cb527aaf8110926a110066a/701e9/WMXH9Ti.png 1780w", "/static/8fd1025e5cb527aaf8110926a110066a/c7bb6/WMXH9Ti.png 1986w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/8fd1025e5cb527aaf8110926a110066a/4ef49/WMXH9Ti.png",
              "alt": "WMXH9Ti",
              "title": "WMXH9Ti",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`Scraping geonames’ search using Python and `}<a parentName="p" {...{
        "href": "https://pythonhosted.org/pyquery/"
      }}>{`pyquery`}</a>{` looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`def countynames():
    with open("h1bs-2012-2016-cleaned.csv", "rb") as csvfile:
        reader = csv.reader(csvfile)
        reader.next()
        with open("h1bs-2012-2016-final.csv", "wb") as csvoutfile:
            writer = csv.writer(csvoutfile)

            writer.writerow(['employer',
                             'job title',
                             'base salary',
                             'city',
                             'county',
                             'state',
                             'submit date',
                             'start date',
                             'case status'])

            counties = {}

            for row in reader:
                if len(row) > 0:
                    location = row[3]

                    if location not in counties:
                        url = "http://geonames.net/search.html?q=%s&username=YOUR_USERNAME" % location.replace(' ', '+').replace(',', '')

                        d = pq(url)
                        county = d('table.restable tr:eq(2) td:eq(2) small').text()
                        counties[location] = county

                    _location = location.split(',')

                    outrow = [row[0],
                                    row[1],
                                     row[2],
                                     _location[0].strip(),
                                     counties[location],
                                     _location[1],
                                     row[4],
                                     row[5],
                                     row[6]]
                    print outrow
                    writer.writerow(outrow)
`}</code></pre>
    <p>{`Ho boy, so much code! Let me walk you through it.`}</p>
    <p>{`We start by opening the input file - `}<inlineCode parentName="p">{`with open(file) as csvfile:`}</inlineCode>{`. The rest of our code works with this file. When we’re done, Python will close the file pointer and do any cleanup.`}</p>
    <p>{`Then we create a `}<inlineCode parentName="p">{`csvreader`}</inlineCode>{` and skip the header with `}<inlineCode parentName="p">{`reader.next()`}</inlineCode>{`. We follow by opening the output file, creating a `}<inlineCode parentName="p">{`csvwriter`}</inlineCode>{`, and writing a header row with `}<inlineCode parentName="p">{`writerow`}</inlineCode>{`.`}</p>
    <p>{`We’re going to use a `}<inlineCode parentName="p">{`counties`}</inlineCode>{` dictionary to avoid pinging Geonames for things we already know. We loop through rows in our input file with `}<inlineCode parentName="p">{`for row in reader`}</inlineCode>{`. Each row is now an array of strings called `}<inlineCode parentName="p">{`row`}</inlineCode>{`.`}</p>
    <p><inlineCode parentName="p">{`location`}</inlineCode>{` is in `}<inlineCode parentName="p">{`row[3]`}</inlineCode>{`. If we don’t have it yet, we make a request to `}<inlineCode parentName="p">{`geonames.net/search.html`}</inlineCode>{` with a `}<inlineCode parentName="p">{`q`}</inlineCode>{` search term and a `}<inlineCode parentName="p">{`username`}</inlineCode>{`. This lets us use the donate-only service, which is faster and more reliable. We’re also making a lot of requests – not paying would be a dick move.`}</p>
    <p><inlineCode parentName="p">{`pq(url)`}</inlineCode>{` gives us a jQuery-like interface to the HTML response. We access the `}<inlineCode parentName="p">{`<small>`}</inlineCode>{` in 2nd column of 2nd row of table with class restable with `}<inlineCode parentName="p">{`d('table.restable tr:eq(2) td:eq(2) small')`}</inlineCode>{` then get its `}<inlineCode parentName="p">{`text()`}</inlineCode>{` value. That’s the county name.`}</p>
    <p>{`I had to inspect source on the geonames website to find that lookup. This code is fragile, and it will break if geonames changes anything. But the search results page looks like it hasn’t been redesigned in about 10 years, so I think we’re good.`}</p>
    <p>{`With the county name fetched, we cache the result in `}<inlineCode parentName="p">{`counties`}</inlineCode>{` and write a new row into the output file with `}<inlineCode parentName="p">{`writer.writerow()`}</inlineCode>{`.`}</p>
    <p>{`Ain’t Python grand?`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      